import * as C from 'app/utils/constants';
import {alphanumericSort} from 'app/utils/featureUtils';

import {FeatureId, NumberedFeature} from './ManageFeaturesProvider';

import {ManagePropertiesMode} from '.';

// Portfolio-wide acreage cap, enforced only as an aggregate banner (not a
// per-row issue).
export const PORTFOLIO_MAX_ACRES = 3_000_000;

// Minimum per-property area (mirrors the backend MIN_AREA_ACRES). Used to phrase
// the too-small issue in terms of the limit rather than the feature's own size.
const MIN_AREA_ACRES = 0.1;

export type RowActionKind = 'split' | 'setName' | 'rename';

// One problem with a feature, shown inside the row's expanded fold-out. Each
// issue carries its own fix (action) next to it, or null when it can't be fixed
// in the uploader (e.g. too small / too spread out).
export interface RowIssue {
  id: string;
  // Which location (feature) the issue belongs to — a fix (e.g. Split) acts on
  // this specific feature even when several are grouped into one row.
  featureId: FeatureId;
  title: string;
  description: string;
  action: {kind: RowActionKind; label: string} | null;
}

export interface RowView {
  feature: NumberedFeature;
  // Empty = "Ready"; otherwise the row is in "Needs attention".
  issues: RowIssue[];
  // Not uploadable as-is → checkbox disabled. Name conflicts are non-blocking.
  blocking: boolean;
}

// One location within a property — a single feature. Mirrors a `subRow` in the
// portfolio property table (usePropertyTable.tsx).
export interface LocationRow {
  feature: NumberedFeature;
  partName: string; // '' renders as the italic "Location" placeholder
  issues: RowIssue[];
  blocking: boolean;
}

// One table row = one property, grouped by name. `locations` mirrors the
// property table's parent `features` / `subRows`: length 1 is a single-location
// property (rendered like a normal row); length > 1 is a multi-location
// property (parent row that expands to its location subrows).
export interface PropertyGroup {
  key: string;
  name: string;
  locations: LocationRow[]; // sorted by part name
  blocking: boolean; // any location blocks upload
  // Section assignment signals. A group can have both — when it does it
  // belongs to "Warnings" (warnings take precedence over errors so they
  // don't get hidden behind blocking issues the user has to fix anyway).
  hasWarning: boolean; // any location has a rename-fixable name conflict
  hasErrors: boolean; // any location has a non-rename (blocking) issue
}

// Short title from a backend problem string ("Feature area is too small (…)" →
// "Feature area is too small"); the parenthetical detail stays in the body.
function problemTitle(problem: string): string {
  const parenIndex = problem.indexOf(' (');
  return parenIndex > 0 ? problem.slice(0, parenIndex) : problem;
}

/**
 * Classify a feature into the issues the redesigned table renders inside its
 * expanded fold-out. Pure; the View memoizes a RowView per feature and the
 * Table maps each issue's action to a handler (Split / focus the name input).
 *
 * The portfolio-acreage cap is deliberately NOT a row issue — it's an
 * aggregate concern surfaced only in the top banner (collectionErrors).
 */
export function categorizeFeature(
  feature: NumberedFeature,
  opts: {name: string | undefined; warnings: string[] | null; mode: ManagePropertiesMode}
): RowView {
  const {name, warnings, mode} = opts;
  const problems: string[] = feature.properties?.['__UPSTREAM_PROBLEMS'] ?? [];
  const isOverlay = mode === 'overlay' || mode === 'updateOverlay';
  const missingName = !name && !isOverlay;
  const tooLargeText = C.UPLOAD_ERROR_CONSTANTS.TOO_LARGE_AREA.text;
  const tooSmallText = C.UPLOAD_ERROR_CONSTANTS.TOO_SMALL_AREA.text;

  const issues: RowIssue[] = [];

  for (const problem of problems) {
    if (problem.startsWith(tooLargeText)) {
      issues.push({
        id: 'too-large',
        featureId: feature.id,
        title: 'Over the per-property size limit',
        description: 'Split this property into smaller sites to import it.',
        action: {kind: 'split', label: 'Split'},
      });
    } else if (problem.startsWith(tooSmallText)) {
      // State the minimum rather than echoing the feature's tiny measured size.
      issues.push({
        id: 'too-small',
        featureId: feature.id,
        title: 'Feature area is too small',
        description: `Feature area is below the minimum of ${MIN_AREA_ACRES} acres.`,
        action: null,
      });
    } else {
      // Too spread out / too many coordinates / invalid geometry — nothing the
      // user can fix in the uploader. The full backend string keys it (and is
      // unique), so distinct problems stay distinct.
      issues.push({
        id: problem,
        featureId: feature.id,
        title: problemTitle(problem),
        description: problem,
        action: null,
      });
    }
  }

  if (missingName) {
    issues.push({
      id: 'missing-name',
      featureId: feature.id,
      title: 'Missing a name',
      description: 'Give this property a name to import it.',
      action: {kind: 'setName', label: 'Set name'},
    });
  }

  for (const warning of warnings ?? []) {
    if (warning.startsWith('Property name matches')) {
      issues.push({
        id: 'name-conflict',
        featureId: feature.id,
        title: 'Name conflicts with existing property',
        description: warning,
        action: {kind: 'rename', label: 'Rename'},
      });
    }
  }

  // Everything blocks upload except a name conflict (you can import with a
  // duplicate name; it's only a warning).
  const blocking = issues.some((issue) => issue.action?.kind !== 'rename');

  return {feature, issues, blocking};
}

// Sort locations by part name, empties last, ties broken by id.
// `alphanumericSort` handles numbers, mixed types, null/empty, and pads
// numeric segments so part names like "1", "2", "10" order correctly
// (post-split they always look like that).
function sortLocations(a: LocationRow, b: LocationRow): number {
  const byPartName = alphanumericSort(a.partName, b.partName);
  if (byPartName !== 0) return byPartName;
  return a.feature.id - b.feature.id;
}

/**
 * Group features into properties by display name, mirroring the portfolio
 * property table (PropertyOverview.tsx groupedFeatures + usePropertyTable's
 * {features, subRows}). Single-name features yield single-location properties;
 * duplicate names collapse into one multi-location property whose locations are
 * the per-feature subrows, sorted by part name. Groups key on the committed
 * display name, matching how the backend combines same-named features.
 */
export function buildPropertyGroups(opts: {
  features: NumberedFeature[];
  removedFeatureIds: ReadonlySet<FeatureId> | {has: (id: FeatureId) => boolean};
  // Editable display name (from nameValues), used for grouping, the rendered
  // name, and the missing-name check.
  nameOf: (feature: NumberedFeature) => string;
  warningsOf: (feature: NumberedFeature) => string[] | null;
  partNameOf: (feature: NumberedFeature) => string;
  mode: ManagePropertiesMode;
}): PropertyGroup[] {
  const {features, removedFeatureIds, nameOf, warningsOf, partNameOf, mode} = opts;

  // Preserve first-appearance order of groups (features arrive name-sorted).
  const order: string[] = [];
  const byName = new Map<string, LocationRow[]>();

  for (const feature of features) {
    if (!feature.properties || removedFeatureIds.has(feature.id)) continue;
    const {issues, blocking} = categorizeFeature(feature, {
      name: nameOf(feature),
      warnings: warningsOf(feature),
      mode,
    });
    const location: LocationRow = {feature, partName: partNameOf(feature), issues, blocking};
    // Overlays never group; key on feature.id so each row is its own bucket.
    const key =
      mode === 'overlay' || mode === 'updateOverlay' ? String(feature.id) : nameOf(feature);
    let locations = byName.get(key);
    if (!locations) {
      locations = [];
      byName.set(key, locations);
      order.push(key);
    }
    locations.push(location);
  }

  return order.map((key) => {
    const locations = byName.get(key)!.slice().sort(sortLocations);
    return {
      key,
      name: nameOf(locations[0].feature),
      locations,
      blocking: locations.some((location) => location.blocking),
      hasWarning: locations.some((location) =>
        location.issues.some((issue) => issue.action?.kind === 'rename')
      ),
      hasErrors: locations.some((location) =>
        location.issues.some((issue) => issue.action?.kind !== 'rename')
      ),
    };
  });
}
